import { useRef, useState } from "react";
import { Loader2, Upload, Images, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useMedia, useUpload, formatBytes } from "@/lib/media";
import { cn } from "@/lib/utils";

export const UploadButton = ({ onUploaded, multiple = false, className, children, testid = "upload-button", iconOnly = false }) => {
  const input = useRef(null);
  const [pending, setPending] = useState(0);
  const upload = useUpload();
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setPending(files.length);
    for (const f of files) {
      try {
        const item = await upload.mutateAsync(f);
        onUploaded?.(item);
      } catch {
        // toast already shown
      }
      setPending((p) => p - 1);
    }
    toast.success(files.length > 1 ? `${files.length} images uploaded` : "Image uploaded");
  };
  return (
    <>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple={multiple} className="hidden" onChange={onFiles} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => input.current?.click()} disabled={pending > 0} data-testid={testid} className={cn("inline-flex items-center justify-center gap-2 disabled:opacity-60", className)}>
        {pending > 0 ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {!iconOnly && (children ?? (pending > 0 ? `Uploading ${pending}…` : "Upload"))}
      </button>
    </>
  );
};

export const MediaPicker = ({ open, onOpenChange, onSelect }) => {
  const { data: items = [], isLoading } = useMedia();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden border-white/10 bg-surface p-0" data-testid="media-picker">
        <DialogHeader className="border-b border-white/10 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle className="font-display">Media library</DialogTitle>
              <DialogDescription>Pick an uploaded image or upload a new one.</DialogDescription>
            </div>
            <UploadButton testid="media-picker-upload" onUploaded={(item) => { onSelect(item.url); onOpenChange(false); }} className="btn-crimson !py-2 !px-4 !text-xs">Upload new</UploadButton>
          </div>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto p-6" data-lenis-prevent>
          {isLoading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div> : items.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-500">No uploads yet. Use "Upload new" to add your first photo.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {items.map((m) => (
                <button key={m.id} type="button" onClick={() => { onSelect(m.url); onOpenChange(false); }} data-testid={`media-pick-${m.id}`} className="group overflow-hidden rounded-md border border-white/10 bg-obsidian text-left transition-colors hover:border-crimson">
                  <div className="flex h-28 items-center justify-center bg-[radial-gradient(circle_at_center,#1f2632,#0a0c10)]"><img src={m.url} alt={m.filename} className="max-h-28 w-full object-contain" loading="lazy" /></div>
                  <div className="px-3 py-2"><p className="truncate text-xs text-slate-200">{m.filename}</p><p className="font-mono text-[10px] text-slate-500">{formatBytes(m.size)}</p></div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ImageField = ({ value, onChange, testid = "image-field", placeholder = "Paste a URL or upload", compact = false }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className={cn("flex items-center gap-2", compact && "w-full")}>
      {value ? (
        <span className={cn("flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-obsidian", compact ? "h-9 w-12" : "h-11 w-16")}>
          <img src={value} alt="" className="h-full w-full object-contain" />
        </span>
      ) : null}
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={`${testid}-input`} className={cn("field min-w-0 flex-1", compact && "!py-2 text-xs")} />
      <UploadButton iconOnly testid={`${testid}-upload`} onUploaded={(item) => onChange(item.url)} className={cn("shrink-0 rounded-md border border-white/15 text-slate-200 hover:border-white/40 hover:bg-white/5", compact ? "h-9 w-9" : "h-11 w-11")} />
      <button type="button" onClick={() => setPickerOpen(true)} title="Choose from library" data-testid={`${testid}-library`} className={cn("shrink-0 rounded-md border border-white/15 text-slate-200 hover:border-white/40 hover:bg-white/5 inline-flex items-center justify-center", compact ? "h-9 w-9" : "h-11 w-11")}><Images className="h-4 w-4" /></button>
      <MediaPicker open={pickerOpen} onOpenChange={setPickerOpen} onSelect={onChange} />
    </div>
  );
};

export const MediaCard = ({ item, onDelete, onCopy }) => (
  <div className="admin-card group overflow-hidden" data-testid={`media-item-${item.id}`}>
    <div className="flex h-44 items-center justify-center bg-[radial-gradient(circle_at_center,#1f2632,#0a0c10)]"><img src={item.url} alt={item.filename} className="max-h-44 w-full object-contain" loading="lazy" /></div>
    <div className="flex items-start justify-between gap-3 p-4">
      <div className="min-w-0">
        <p className="truncate font-display text-sm font-semibold">{item.filename}</p>
        <p className="mt-0.5 font-mono text-[10px] text-slate-500">{formatBytes(item.size)} · {item.content_type.replace("image/", "").toUpperCase()}</p>
      </div>
      <div className="flex shrink-0 gap-2">
        <button type="button" onClick={onCopy} title="Copy URL" data-testid={`media-copy-${item.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-white/40 hover:text-white"><Check className="h-4 w-4" /></button>
        <button type="button" onClick={onDelete} title="Delete" data-testid={`media-delete-${item.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-crimson hover:text-crimson"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  </div>
);
